// features/auth/loginSlice.js - FIXED logout action
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginAPI } from '../auth/loginAPI';

// Async thunks for different login types
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await loginAPI.login(email, password);
      
      if (response && response.ok) {
        return response.userdata;
      } else {
        return rejectWithValue('Invalid login credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed. Please try again.');
    }
  }
);

export const parentLoginUser = createAsyncThunk(
  'auth/parentLogin',
  async ({ studentId, parentMobile }, { rejectWithValue }) => {
    try {
      const response = await loginAPI.parentLogin(studentId, parentMobile);
      
      if (response && response.ok) {
        return { ...response.studData, loginType: 'parent' };
      } else {
        return rejectWithValue('Invalid parent credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Parent login failed');
    }
  }
);

export const fleetLoginUser = createAsyncThunk(
  'auth/fleetLogin',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await loginAPI.fleetLogin(email, password);
      
      if (response && response.ok) {
        return { ...response.userdata, loginType: 'fleet' };
      } else {
        return rejectWithValue('Invalid fleet credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Fleet login failed');
    }
  }
);

export const aargeeLoginUser = createAsyncThunk(
  'auth/aargeeLogin',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await loginAPI.aargeeLogin(email, password);
      
      if (response && response.ok) {
        return { ...response.userdata, loginType: 'aargee' };
      } else {
        return rejectWithValue('Invalid credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, mobile }, { rejectWithValue }) => {
    try {
      const response = await loginAPI.getUserByEmailAndMobile(email, mobile);
      
      if (response && response.length > 0) {
        return { user: response[0], success: true };
      } else {
        return rejectWithValue('No user found with these credentials');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to find user');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, newPassword, confirmPassword, currentPassword = '', type = 'dealer' }, { rejectWithValue }) => {
    if (newPassword !== confirmPassword) {
      return rejectWithValue('Passwords do not match');
    }
    
    if (newPassword.length < 4) {
      return rejectWithValue('Password must be at least 4 characters');
    }

    if (currentPassword && newPassword === currentPassword) {
      return rejectWithValue('New password must be different from your current password');
    }
    
    try {
      const response = await loginAPI.changePassword(email, newPassword, confirmPassword, type);
      return { success: true, message: 'Password updated successfully', data: response };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update password');
    }
  }
);

// ✅ FIXED: Simplified logout action
const clearAuthStorage = () => {
  [
    'admin_user',
    'fleetuser',
    'alcotrackerparentuser',
    'aargeeuser',
    'isLoggedIn',
    'loginTimestamp',
    'alcobrakeadminreloader',
    'alcobrakeadminfleetreloader',
    'alcobrakeadminaargeereloader',
    'isDrivesync',
    'dealerId',
  ].forEach((key) => localStorage.removeItem(key));
};

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    clearAuthStorage();
    sessionStorage.setItem('justLoggedOut', 'true');
    return null;
  }
);

// Helper function to get user from storage
const getUserFromStorage = () => {
  // Check different user types
  const user = localStorage.getItem('admin_user');
  const fleetUser = localStorage.getItem('fleetuser');
  const parentUser = localStorage.getItem('alcotrackerparentuser');
  const aargeeUser = localStorage.getItem('aargeeuser');
  
  if (user) {
    const parsedUser = JSON.parse(user);
    return {
      user: parsedUser,
      isAuthenticated: true,
      loginType: 'dealer',
      userRole: parsedUser.role || parsedUser.accesslevel || 'dealer',
      userName: parsedUser.userName || parsedUser.dealerName || parsedUser.name || parsedUser.emailId || 'User'
    };
  } else if (fleetUser) {
    const parsedUser = JSON.parse(fleetUser);
    return {
      user: parsedUser,
      isAuthenticated: true,
      loginType: 'fleet',
      userRole: 'fleet',
      userName: parsedUser.name || parsedUser.emailId || 'Fleet User'
    };
  } else if (parentUser) {
    const parsedUser = JSON.parse(parentUser);
    return {
      user: parsedUser,
      isAuthenticated: true,
      loginType: 'parent',
      userRole: 'parent',
      userName: parsedUser.name || 'Parent User'
    };
  } else if (aargeeUser) {
    const parsedUser = JSON.parse(aargeeUser);
    return {
      user: parsedUser,
      isAuthenticated: true,
      loginType: 'aargee',
      userRole: 'aargee',
      userName: parsedUser.name || parsedUser.emailId || 'Aargee User'
    };
  }
  
  return {
    user: null,
    isAuthenticated: false,
    loginType: null,
    userRole: null,
    userName: null
  };
};

const initialState = {
  ...getUserFromStorage(),
  loading: false,
  error: null,
  successMessage: null,
  forgotPasswordData: null,
  resetPasswordData: null
};

const loginSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    clearForgotPasswordData: (state) => {
      state.forgotPasswordData = null;
    },
    clearResetPasswordData: (state) => {
      state.resetPasswordData = null;
    },
    resetAuthState: (state) => {
      clearAuthStorage();
      sessionStorage.setItem('justLoggedOut', 'true');
      state.user = null;
      state.isAuthenticated = false;
      state.loginType = null;
      state.userRole = null;
      state.userName = null;
      state.loading = false;
      state.error = null;
      state.successMessage = null;
      state.forgotPasswordData = null;
      state.resetPasswordData = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.userRole = action.payload?.role || action.payload?.accesslevel || 'dealer';
      state.userName = action.payload?.userName || action.payload?.dealerName || action.payload?.name || action.payload?.emailId;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loginType = 'dealer';
        state.userRole = action.payload?.role || action.payload?.accesslevel || 'dealer';
        state.userName = action.payload?.userName || action.payload?.dealerName || action.payload?.name || action.payload?.emailId;
        state.successMessage = 'Login successful!';
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload || 'Login failed. Please check your credentials.';
      })
      
      // Parent Login
      .addCase(parentLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(parentLoginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loginType = 'parent';
        state.userRole = 'parent';
        state.userName = action.payload?.name || 'Parent User';
        state.successMessage = 'Parent login successful!';
      })
      .addCase(parentLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fleet Login
      .addCase(fleetLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fleetLoginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loginType = 'fleet';
        state.userRole = 'fleet';
        state.userName = action.payload?.userName || action.payload?.name || action.payload?.emailId || 'Fleet User';
        state.successMessage = 'Fleet login successful!';
      })
      .addCase(fleetLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Aargee Login
      .addCase(aargeeLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(aargeeLoginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loginType = 'aargee';
        state.userRole = 'aargee';
        state.userName = action.payload?.userName || action.payload?.name || action.payload?.emailId || 'Aargee User';
        state.successMessage = 'Login successful!';
      })
      .addCase(aargeeLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Forgot password cases
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordData = action.payload;
        state.successMessage = 'User verified! Please enter new password.';
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'User not found. Please check your credentials.';
      })
      
      // Reset password cases
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.resetPasswordData = action.payload;
        state.successMessage = action.payload.message || 'Password updated successfully!';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update password. Please try again.';
      })
      
      // ✅ FIXED: Logout case - complete state reset
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loginType = null;
        state.userRole = null;
        state.userName = null;
        state.loading = false;
        state.error = null;
        state.successMessage = null;
        state.forgotPasswordData = null;
        state.resetPasswordData = null;
      });
  }
});

// Actions
export const { 
  clearMessages, 
  clearForgotPasswordData, 
  clearResetPasswordData,
  resetAuthState,
  setUser
} = loginSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectLoginType = (state) => state.auth.loginType;
export const selectUserRole = (state) => state.auth.userRole;
export const selectUserName = (state) => state.auth.userName;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthSuccessMessage = (state) => state.auth.successMessage;
export const selectForgotPasswordData = (state) => state.auth.forgotPasswordData;
export const selectResetPasswordData = (state) => state.auth.resetPasswordData;

export default loginSlice.reducer;
